import { readSessionActor } from "@/lib/analytics/actor";
import {
  ADDRESS_ONBOARDING_ENABLED,
  firstLoginGateApplies,
  needsOnboarding,
  needsPassword
} from "@/lib/first-login/gate";
import {
  ACTIVATE_PATH,
  ONBOARDING_PATH,
  SET_PASSWORD_GATE_SOURCE
} from "@/lib/navigation/routes";
import { addressesQuery } from "@/queries/customer/addresses";
import { profileQuery } from "@/queries/customer/customer";
import { queryClient } from "@/queries/queryClient";
import { createRoute, Outlet, redirect } from "@tanstack/react-router";
import { rootRoute } from "./$rootRoute";

/**
 * The delivery addresses, or `null` if they cannot be read.
 *
 * `fetchQuery`, not `ensureQueryData`: saving an address invalidates this query,
 * and `ensureQueryData` would keep serving the stale empty list from the cache -
 * sending a salon that just onboarded straight back to onboarding.
 */
async function readAddresses() {
  try {
    const { addresses } = await queryClient.fetchQuery(addressesQuery());
    return addresses;
  } catch {
    return null;
  }
}

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "auth", // Pathless route - no path property
  beforeLoad: async ({ location }) => {
    // Where this navigation was heading, query string and fragment included, for
    // whichever redirect below interrupts it (`safeRedirectTarget` keeps the
    // fragment on the way back, so dropping it here would not round-trip). From
    // the router's own `location`, never `window.location`: during a
    // client-side navigation the latter still names the page being left. The
    // router's `hash` carries no leading `#`.
    const here = `${location.pathname}${location.searchStr ?? ""}${
      location.hash ? `#${location.hash}` : ""
    }`;

    // Check authentication before loading any child routes
    // Every redirect below is thrown *outside* the try: a thrown `redirect` is
    // an exception like any other, and an enclosing catch would turn it into a
    // login redirect.
    let data;
    try {
      data = await queryClient.ensureQueryData(profileQuery());
    } catch {
      // If customer.retrieve fails, redirect to login
      throw redirect({ to: "/login", search: { redirect: here } });
    }

    if (!data.customer.id) {
      throw redirect({ to: "/login", search: { redirect: here } });
    }

    // First-login gates, in order. Both carry `here` so the page the salon was
    // heading to survives the whole chain.
    //
    // The cheap checks (route, actor) come first and nothing is read for a
    // navigation the gate could not redirect: every navigation to /activate,
    // /onboarding or in an operator session would otherwise pay for a fetch
    // whose answer is ignored.
    const scope = {
      pathname: location.pathname,
      actorType: readSessionActor()
    };

    // 1. Set a password (KAN-306). A salon that arrived through a passwordless
    //    link has no other way back in once that link is spent. Read off the
    //    profile fetched above, so it adds no request.
    if (needsPassword({ ...scope, hasPassword: data.customer.has_password })) {
      throw redirect({
        to: ACTIVATE_PATH,
        search: { redirect: here, source: SET_PASSWORD_GATE_SOURCE },
        replace: true
      });
    }

    // 2. First-login onboarding (KAN-293): a salon with no default shipping
    //    address cannot check out, so ask for one once the password is set.
    //    Currently switched off (`ADDRESS_ONBOARDING_ENABLED`); the flag is
    //    checked first so no address list is read while it is off.
    if (
      ADDRESS_ONBOARDING_ENABLED &&
      firstLoginGateApplies(scope) &&
      needsOnboarding({
        ...scope,
        addresses: await readAddresses()
      })
    ) {
      throw redirect({
        to: ONBOARDING_PATH,
        search: { redirect: here },
        replace: true
      });
    }

    // Return user data to be available in route context
    return {
      userId: data.customer.id,
      userEmail: data.customer.email
    };
  },
  component: Outlet // Just render children
});
